/**
 * Petmall — price extras inside the Recharge subscription widget.
 *
 * Two additions, both rendered into the widget's shadow root and re-applied whenever it
 * repaints (variant change, option switch):
 *
 * 1. Subscription option price. The plans use Recharge "dynamic pricing" (-15% on the first
 *    order, -5% after), and in that mode the widget renders only the struck-through original
 *    price plus the explanatory line — no price at all. The first-order price is computed
 *    from the selected variant and the plan's own discount, read from the product feed the
 *    widget itself loads (static.rechargecdn.com/.../product/2022-06/<product-id>.json).
 *
 * 2. Unit price under the one-time purchase price ("7,96 € / кг"), mirroring
 *    snippets/price-per-unit.liquid: pieces before weight, same integer-cent rounding and
 *    the same max-per-kg setting, so it matches the value shown next to the variant.
 *
 * 3. The subscription option's Figma layout: repeat-purchase icon before its label,
 *    "Цена за първата доставка", the plan's benefits and the pill-shaped frequency dropdown.
 */
(function () {
  const UNIT_CLASS = 'petmall-ppu';
  const PRICE_CLASS = 'petmall-sub-price';
  const DETAILS_CLASS = 'petmall-sub-details';
  const ICON_CLASS = 'petmall-sub-icon';
  /** Every plan charges 5% off the regular price from the second delivery on. */
  const NEXT_ORDER_DISCOUNT = 5;
  const BENEFITS = [
    ['Спестяваш ' + NEXT_ORDER_DISCOUNT + '%', ' от редовната цена при всяка следваща доставка'],
    ['Автоматична', ' доставка през избран от теб период'],
    ['Пълен контрол', ' – променяш, пропускаш или отменяш по всяко време'],
  ];
  const STYLE_ID = 'petmall-ppu-style';
  const PLAN_FEED = 'https://static.rechargecdn.com/store/{shop}/product/2022-06/{product}.json';

  /** { productId: {initial: 15, initialType: 'percentage'} | null } — null = no dynamic plan. */
  const planCache = {};

  const config = (() => {
    try {
      return JSON.parse(document.getElementById('PetmallPPUConfig').textContent);
    } catch (e) {
      return { maxKg: 100 };
    }
  })();

  function selectedVariant() {
    const node = document.querySelector('variant-selects [data-selected-variant]');
    if (!node || !node.textContent) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /** Piece count or weight for the unit price, or null when the variant has neither. */
  function unitInfo(variant) {
    if (!variant) return null;
    const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    for (const option of options) {
      const match = String(option).toLowerCase().match(/(\d+(?:[.,]\d+)?)\s*(бр|br)/);
      if (match) {
        const count = parseFloat(match[1].replace(',', '.'));
        if (count > 0) return { pieces: count, unit: 'бр' };
      }
    }
    const grams = Number(variant.weight || 0);
    if (grams > 0) return { grams: grams, unit: 'кг', maxCents: (Number(config.maxKg) || 100) * 100 };
    return null;
  }

  /** Same integer-cent arithmetic as snippets/price-per-unit.liquid, so both show 37,29. */
  function unitCents(priceCents, info) {
    if (info.pieces) return Math.floor(priceCents / info.pieces);
    return Math.floor((priceCents * 1000) / info.grams);
  }

  /**
   * The price spans carry a visually-hidden label (", Discounted price:") whose comma would
   * be read as a decimal separator, so strip those before parsing.
   */
  function visibleText(el) {
    const clone = el.cloneNode(true);
    clone.querySelectorAll('.visually-hidden, [hidden], [aria-hidden="true"]').forEach((n) => n.remove());
    return clone.textContent;
  }

  /** "€54,90" / "€1.234,56" → 54.9 / 1234.56 */
  function parseMoney(text) {
    let value = String(text || '').replace(/[^\d.,]/g, '');
    if (!value) return NaN;
    if (value.includes('.') && value.includes(',')) value = value.replace(/\./g, '').replace(',', '.');
    else if (value.includes(',')) value = value.replace(',', '.');
    return parseFloat(value);
  }

  function formatMoney(cents) {
    if (window.FoxTheme && FoxTheme.Currency && FoxTheme.settings) {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function ensureStyle(root) {
    if (root.getElementById && root.getElementById(STYLE_ID)) return;
    if (root.querySelector('#' + STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.' + UNIT_CLASS + '{display:block;margin-top:.2rem;font-size:1.3rem;line-height:1.3;' +
      'font-weight:400;opacity:.65;text-decoration:none;}' +
      /* Recharge's own sheet is adopted, so it outranks this <style> at equal specificity. */
      '.rc-purchase-option__selector{font-size:1.6rem!important;line-height:2.4rem!important;}' +
      '.' + ICON_CLASS + '{flex:0 0 auto;width:24px;height:24px;display:block;margin-inline-end:8px;}' +
      /* Prices and details start at the card edge, under the indicator (Figma). */
      '[part~="rc-purchase-option__prices"]{margin-inline-start:0!important;margin-top:.8rem!important;}' +
      '.rc-purchase-option__label{padding-bottom:1.2rem!important;}' +
      'rc-radio-icon{display:inline-flex;align-items:center;justify-content:center;' +
      'flex:0 0 25px;width:25px;height:25px;}' +
      (config.checkedIcon
        ? /* The icon's svg is sized in em inside its own shadow root, so a zero font-size
             hides it and the design's filled check shows as the background. */
          'rc-radio-icon[checked]{font-size:0;background:url("' + config.checkedIcon + '") center/25px 25px no-repeat;}'
        : '') +
      '.' + PRICE_CLASS + '{display:block;font-size:1.8rem;line-height:1.25;font-weight:600;' +
      'color:#F94144;text-decoration:none;}' +
      '@media (min-width:750px){.' + PRICE_CLASS + '{font-size:2.4rem;}}' +
      '.' + DETAILS_CLASS + '{display:block;width:100%;margin-top:.2rem;color:#2B2B2B;' +
      'font-size:1.4rem;line-height:2rem;font-weight:400;cursor:default;}' +
      '.' + DETAILS_CLASS + '__caption{display:block;color:#7A7D82;font-size:1.2rem;line-height:1.8rem;}' +
      '.' + DETAILS_CLASS + '__divider{display:block;height:1px;margin:.8rem 0 1.6rem;' +
      'background:#DFDFDF;border:0;}' +
      '.' + DETAILS_CLASS + '__list{display:flex;flex-direction:column;gap:.4rem;margin:0;padding:0;' +
      'list-style:none;}' +
      '.' + DETAILS_CLASS + '__item{display:flex;align-items:flex-start;gap:.8rem;}' +
      '.' + DETAILS_CLASS + '__item img{flex:0 0 auto;width:16px;height:16px;margin-top:2px;}' +
      '.' + DETAILS_CLASS + '__item strong{font-weight:700;}' +
      /* Frequency dropdown as the design's pill. */
      'rc-selling-plans::part(rc-plans-select){height:52px!important;padding:0 5.6rem 0 2.4rem!important;' +
      'background-color:#ffffff!important;border:1px solid #949494!important;border-radius:100px!important;' +
      'color:#111111!important;font-size:1.4rem!important;line-height:2rem!important;' +
      'font-weight:600!important;letter-spacing:.028px;}';
    root.appendChild(style);
  }

  /**
   * The plan's initial discount, from the same feed the widget loads (so it is served from
   * cache). All plans of a product share one discount, so the first subscription plan wins.
   */
  function planDiscount(widget) {
    const productId = widget.getAttribute('product-id');
    if (!productId) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(planCache, productId)) {
      return Promise.resolve(planCache[productId]);
    }
    const shop = (window.Shopify && window.Shopify.shop) || '';
    if (!shop) return Promise.resolve(null);

    const url = PLAN_FEED.replace('{shop}', shop).replace('{product}', productId);
    return fetch(url)
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        const plans = (data && data.product && data.product.plans) || [];
        const plan = plans.find(function (p) {
          return p.type === 'subscription' && p.pricing_progression && p.pricing_progression.length;
        });
        const amount = plan ? parseFloat(plan.discount_amount) : NaN;
        planCache[productId] = plan && isFinite(amount) && amount > 0
          ? { amount: amount, type: plan.discount_type || 'percentage' }
          : null;
        return planCache[productId];
      })
      .catch(function () {
        planCache[productId] = null;
        return null;
      });
  }

  function discountedCents(priceCents, discount) {
    if (!discount) return 0;
    if (discount.type === 'percentage') return Math.round(priceCents * (1 - discount.amount / 100));
    return Math.max(0, priceCents - Math.round(discount.amount * 100));
  }

  /**
   * Dynamic-pricing options show no price of their own — render the first-order price where
   * the widget would normally put it, above the "-15% / -5%" line.
   */
  function renderSubscriptionPrice(widget, root, variant) {
    const priceCents = variant && Number(variant.price);
    if (!priceCents) return;

    planDiscount(widget).then(function (discount) {
      if (!discount || !widget.shadowRoot) return;
      const current = widget.shadowRoot;
      /* Only this function's own line: the next-delivery line is written synchronously in
         the same pass, and this callback resolves after it. */
      current.querySelectorAll('.' + PRICE_CLASS).forEach((el) => el.remove());

      current.querySelectorAll('[part~="rc-purchase-option__dynamic-price"]').forEach((dynamicEl) => {
        const option = dynamicEl.closest('[part~="rc-purchase-option"]') || dynamicEl.parentElement;
        const container = option && option.querySelector('[part~="rc-purchase-option__prices"]');
        if (!container || container.querySelector('.' + PRICE_CLASS)) return;

        const span = document.createElement('span');
        span.className = PRICE_CLASS;
        span.textContent = formatMoney(discountedCents(priceCents, discount));
        container.insertBefore(span, container.querySelector('.' + DETAILS_CLASS));
      });
    });
  }

  /** Repeat-purchase mark before the "Регулярна доставка" label. */
  function renderOptionIcon(root) {
    if (!config.subscriptionIcon) return;
    const option = root.querySelector('.rc-purchase-option__subscription');
    const selector = option && option.querySelector('.rc-purchase-option__selector');
    if (!selector || selector.querySelector('.' + ICON_CLASS)) return;

    const label = Array.from(selector.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
    );
    if (!label) return;

    const icon = document.createElement('img');
    icon.className = ICON_CLASS;
    icon.src = config.subscriptionIcon;
    icon.alt = '';
    icon.width = 24;
    icon.height = 24;
    selector.insertBefore(icon, label);
  }

  /**
   * "Цена за първата доставка" and the plan's benefits, under the subscription price. They
   * sit in the option's price container rather than the sub-container below it, because
   * that container only exists while the subscription is selected — and these lines are
   * what tell a shopper reading the one-time price what the plan offers.
   */
  function renderSubscriptionDetails(root) {
    const option = root.querySelector('.rc-purchase-option__subscription');
    const container = option && option.querySelector('[part~="rc-purchase-option__prices"]');
    if (!container || container.querySelector('.' + DETAILS_CLASS)) return;

    const details = document.createElement('div');
    details.className = DETAILS_CLASS;

    const caption = document.createElement('span');
    caption.className = DETAILS_CLASS + '__caption';
    caption.textContent = 'Цена за първата доставка';
    details.appendChild(caption);

    const divider = document.createElement('span');
    divider.className = DETAILS_CLASS + '__divider';
    divider.setAttribute('aria-hidden', 'true');
    details.appendChild(divider);

    const list = document.createElement('ul');
    list.className = DETAILS_CLASS + '__list';
    BENEFITS.forEach(([bold, rest]) => {
      const item = document.createElement('li');
      item.className = DETAILS_CLASS + '__item';
      if (config.tickIcon) {
        const tick = document.createElement('img');
        tick.src = config.tickIcon;
        tick.alt = '';
        tick.width = 16;
        tick.height = 16;
        item.appendChild(tick);
      }
      const text = document.createElement('span');
      const strong = document.createElement('strong');
      strong.textContent = bold;
      text.appendChild(strong);
      text.appendChild(document.createTextNode(rest));
      item.appendChild(text);
      list.appendChild(item);
    });
    details.appendChild(list);

    container.appendChild(details);
  }

  function apply(widget) {
    const root = widget.shadowRoot;
    if (!root) return;

    root.querySelectorAll('.' + UNIT_CLASS + ', .' + PRICE_CLASS + ', .' + DETAILS_CLASS).forEach((el) => el.remove());

    const variant = selectedVariant();
    ensureStyle(root);
    renderOptionIcon(root);
    renderSubscriptionDetails(root);
    renderSubscriptionPrice(widget, root, variant);

    const info = unitInfo(variant);
    if (!info) return;

    root.querySelectorAll('[part~="rc-purchase-option__prices"]').forEach((container) => {
      // One-time purchase only — the subscription option shows just its price (Figma).
      const option = container.closest('[part~="rc-purchase-option"]');
      const kind = option && option.querySelector('input[name="purchaseOption"]');
      if (!kind || kind.value !== 'onetime') return;

      const priceEl =
        container.querySelector('[part~="rc-purchase-option__discounted-price"]') ||
        container.querySelector('[part~="rc-purchase-option__price"]');
      if (!priceEl) return;

      const amount = parseMoney(visibleText(priceEl));
      if (!isFinite(amount) || amount <= 0) return;

      const cents = unitCents(Math.round(amount * 100), info);
      if (!cents || (info.maxCents && cents > info.maxCents)) return;

      const span = document.createElement('span');
      span.className = UNIT_CLASS;
      span.textContent = '(' + formatMoney(cents) + ' / ' + info.unit + ')';
      container.appendChild(span);
    });
  }

  function start(widget) {
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        observer.disconnect();
        apply(widget);
        observer.observe(widget.shadowRoot, { childList: true, subtree: true, characterData: true });
      });
    });

    apply(widget);
    observer.observe(widget.shadowRoot, { childList: true, subtree: true, characterData: true });

    // The widget repaints asynchronously after a variant change; re-apply once it settles.
    document.addEventListener('variant:changed', () => {
      window.setTimeout(() => apply(widget), 400);
      window.setTimeout(() => apply(widget), 1200);
    });
  }

  function init() {
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      const widget = document.querySelector('recharge-subscription-widget');
      if (widget && widget.shadowRoot && widget.shadowRoot.querySelector('[part~="rc-purchase-option__prices"]')) {
        window.clearInterval(timer);
        start(widget);
      } else if (tries > 60) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
